import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import { debounce } from "lodash";
import {
    Col,
    Form,
    Button,
    FloatingLabel,
    Dropdown,
    ProgressBar,
} from "react-bootstrap";
import {
    FaEye,
    FaEyeSlash,
    FaChevronRight,
    FaChevronLeft,
    FaCheck,
} from "react-icons/fa";
import { motion } from "framer-motion";
import { toast, ToastContainer } from 'react-toastify';
import { Link, useNavigate } from "react-router-dom";
import logo from "../Images/AZ.png";

const SignUp = () => {
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [currentSection, setCurrentSection] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [products, setProducts] = useState([]);
    const [staff, setStaff] = useState([]);
    const [staffSearch, setStaffSearch] = useState("");
    const [staffAuthStatus, setStaffAuthStatus] = useState(null);
    const [formData, setFormData] = useState({
        full_name: "",
        email_address: "",
        phone_number: "",
        date_of_birth: "",
        id_type: "",
        id_number: "",
        passport_number: "",
        bill_number: "",
        emi_plan_duration: "",
        payment_date: "",
        password: "",
        confirm_password: "",
        product_id: "",
        bill_amount: "",
        staff_name: "",
        staff_password: ""
    });

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const res = await axios.get("https://www.azzenterprises.com/api/products");
                setProducts(res.data);
            } catch (error) {
                toast.error("Failed to load products");
            }
        };

        const fetchStaff = async () => {
            try {
                const res = await axios.get("https://www.azzenterprises.com/api/admin/staff");
                setStaff(res.data);
            } catch (error) {
                toast.error("Failed to load staff");
            }
        };

        fetchProducts();
        fetchStaff();
    }, []);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const idTypes = [
        { value: "NRIC", label: "NRIC" },
        { value: "FIN", label: "FIN" },
        { value: "PASSPORT", label: "Passport" }
    ];

    const emiPlans = [
        { value: "3", label: "3 Months" },
        { value: "4", label: "4 Months" },
        { value: "5", label: "5 Months" },
        { value: "6", label: "6 Months" },
        { value: "7", label: "7 Months" },
        { value: "8", label: "8 Months" },
        { value: "9", label: "9 Months" },
        { value: "10", label: "10 Months" },
        { value: "11", label: "11 Months" },
        { value: "12", label: "12 Months" }
    ];

    const generateBillNumber = (phoneNumber, dateOfBirth) => {
        if (!phoneNumber || !dateOfBirth) return "";

        const phoneDigits = phoneNumber.replace(/\D/g, "");
        const lastThreeDigits = phoneDigits.slice(-3);
        const year = new Date(dateOfBirth).getFullYear();

        return `${lastThreeDigits}${year}`;
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1
        }
    };

    const checkStaffPassword = debounce(async (user_name, password) => {
        try {
            await axios.post("https://www.azzenterprises.com/api/admin/authstaff", { user_name, password });
            setStaffAuthStatus(true);
        } catch {
            setStaffAuthStatus(false);
        }
    }, 700);

    const handleChangeStaff = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (name === "staff_password" && formData.staff_name && value.trim().length > 2) {
            checkStaffPassword(formData.staff_name, value);
        } else {
            setStaffAuthStatus(null);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if ((name === "phone_number" && formData.date_of_birth) ||
            (name === "date_of_birth" && formData.phone_number)) {
            const billNumber = generateBillNumber(
                name === "phone_number" ? value : formData.phone_number,
                name === "date_of_birth" ? value : formData.date_of_birth
            );
            setFormData(prev => ({ ...prev, bill_number: billNumber }));
        }
    };

    const handleSelect = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const validateSection = (section) => {
        const errors = [];

        if (section === 1) {
            if (!formData.full_name.trim()) errors.push("Full name is required");
            if (!formData.email_address.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) errors.push("Invalid email address");
            if (!formData.phone_number.match(/^(?:\+65)?\s?(6|8|9)\d{7}$/)) {
                errors.push("Invalid Singapore phone number (must start with 6, 8, or 9 and be 8 digits)");
            }
            if (!formData.date_of_birth) errors.push("Date of birth is required");
        }

        if (section === 2) {
            if (!formData.password || formData.password.length < 8) errors.push("Password must be at least 8 characters");
            if (formData.password !== formData.confirm_password) errors.push("Passwords do not match");
        }

        if (section === 3) {
            if (!formData.id_type) errors.push("ID type is required");
            if (!formData.id_number.trim()) errors.push("ID number is required");
            if (formData.id_type === "PASSPORT" && !formData.passport_number.trim()) {
                errors.push("Passport number is required");
            }
        }

        if (section === 4) {
            if (!formData.bill_number.trim()) errors.push("Bill number is required");
            if (!formData.bill_amount || isNaN(formData.bill_amount) || formData.bill_amount <= 0) {
                errors.push("Max amount must be a valid number");
            }
            if (!formData.emi_plan_duration) errors.push("EMI plan is required");
            if (!formData.product_id) errors.push("Product selection is required");
            if (!formData.payment_date) errors.push("Payment date is required");
        }

        if (section === 5) {
            if (!formData.staff_name) errors.push("Staff selection is required");
            if (!formData.staff_password.trim()) errors.push("Staff password is required");
        }

        if (errors.length > 0) {
            errors.forEach(error => {
                toast.error(error, {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            });
            return false;
        }

        return true;
    };

    const handleNext = (e) => {
        e.preventDefault();
        const isValid = validateSection(currentSection);
        if (!isValid) return;
        setCurrentSection(prev => prev + 1);
    };

    const handlePrev = () => {
        setCurrentSection(currentSection - 1);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateSection(4)) return;

        setIsSubmitting(true);

        try {
            const userData = {
                ...formData,
            };
            delete userData.confirm_password;
            delete userData.staff_password;

            await axios.post('https://www.azzenterprises.com/api/customers/signup', userData);

            delete userData.password;

            toast.success('Account created successfully! Redirecting to login...', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                onClose: () => {
                    navigate('/sign-in');
                    setIsSubmitting(false);
                }
            });

        } catch (error) {
            setIsSubmitting(false);

            if (error.response) {
                const resData = error.response.data;

                if (resData.errors) {
                    Object.values(resData.errors).forEach(err => toast.error(err[0]));
                } else if (resData.message) {
                    toast.error(resData.message);
                } else if (resData.error) {
                    toast.error(resData.error);
                } else {
                    toast.error('Registration failed. Please try again.');
                }
            } else if (error.request) {
                toast.error('Network error. Please check your connection.');
            } else {
                toast.error('An unexpected error occurred.');
            }
        }
    };

    const completionPercentage = () => {
        let filledSections = 0;

        if (
            formData.full_name.trim() &&
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_address) &&
            /^[\d\s+-]{8,}$/.test(formData.phone_number) &&
            formData.date_of_birth
        ) {
            filledSections++;
        }

        if (
            formData.password.length >= 8 &&
            formData.password === formData.confirm_password
        ) {
            filledSections++;
        }

        const isIDValid =
            formData.id_type &&
            formData.id_number.trim() &&
            (formData.id_type !== "PASSPORT" || formData.passport_number.trim());

        if (isIDValid) {
            filledSections++;
        }

        const isStep4Valid =
            formData.bill_number.trim() &&
            formData.bill_amount && Number(formData.bill_amount) > 0 &&
            formData.emi_plan_duration &&
            formData.product_id;

        if (isStep4Valid) {
            filledSections++;
        }

        if (formData.staff_name && staffAuthStatus === true) {
            filledSections++;
        }

        return Math.round((filledSections / 5) * 100);
    };

    const renderSection = () => {
        switch (currentSection) {
            case 1:
                return (
                    <motion.div variants={containerVariants} initial="hidden" animate="visible">
                        <motion.div variants={itemVariants}>
                            <h5 className="mb-4">Personal Information</h5>
                        </motion.div>

                        <motion.div variants={itemVariants}>
                            <FloatingLabel controlId="full_name" label="Full Name" className="mb-3">
                                <Form.Control
                                    type="text"
                                    name="full_name"
                                    value={formData.full_name}
                                    onChange={handleChange}
                                    placeholder="John Doe"
                                />
                            </FloatingLabel>
                        </motion.div>

                        <motion.div variants={itemVariants}>
                            <FloatingLabel controlId="email_address" label="Email" className="mb-3">
                                <Form.Control
                                    type="email"
                                    name="email_address"
                                    value={formData.email_address}
                                    onChange={handleChange}
                                    placeholder="john@example.com"
                                />
                            </FloatingLabel>
                        </motion.div>

                        <motion.div variants={itemVariants}>
                            <FloatingLabel controlId="phone_number" label="Phone Number" className="mb-3">
                                <Form.Control
                                    type="tel"
                                    name="phone_number"
                                    value={formData.phone_number}
                                    onChange={handleChange}
                                    placeholder="+65 1234 5678"
                                />
                            </FloatingLabel>
                        </motion.div>

                        <motion.div variants={itemVariants}>
                            <FloatingLabel controlId="date_of_birth" label="Date of Birth" className="mb-4">
                                <Form.Control
                                    type="date"
                                    name="date_of_birth"
                                    value={formData.date_of_birth}
                                    onChange={handleChange}
                                />
                            </FloatingLabel>
                        </motion.div>
                    </motion.div>
                );
            case 2:
                return (
                    <motion.div variants={containerVariants} initial="hidden" animate="visible">
                        <motion.div variants={itemVariants}>
                            <h5 className="mb-4">Account Security</h5>
                        </motion.div>

                        <motion.div variants={itemVariants}>
                            <FloatingLabel controlId="password" label="Password" className="mb-3">
                                <Form.Control
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="Create password"
                                />
                                <Button
                                    variant="link"
                                    className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted p-0"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                </Button>
                                <Form.Text className="mt-1 text-muted">
                                    Must be 8 characters at least
                                </Form.Text>
                            </FloatingLabel>
                        </motion.div>

                        <motion.div variants={itemVariants}>
                            <FloatingLabel controlId="confirm_password" label="Confirm Password" className="mb-4">
                                <Form.Control
                                    type={showPassword ? "text" : "password"}
                                    name="confirm_password"
                                    value={formData.confirm_password}
                                    onChange={handleChange}
                                    placeholder="Confirm password"
                                />
                            </FloatingLabel>
                        </motion.div>
                    </motion.div>
                );
            case 3:
                return (
                    <motion.div variants={containerVariants} initial="hidden" animate="visible">
                        <motion.div variants={itemVariants}>
                            <h5 className="mb-4">Identification Details</h5>
                        </motion.div>

                        <motion.div variants={itemVariants}>
                            <Form.Group className="mb-3">
                                <Form.Label>ID Type</Form.Label>
                                <Dropdown>
                                    <Dropdown.Toggle
                                        variant="outline-secondary"
                                        id="id-type-dropdown"
                                        className="w-100 text-start"
                                    >
                                        {formData.id_type || "Select ID type"}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="w-100 p-2 dropdown-scroll">
                                        {idTypes.map(type => (
                                            <Dropdown.Item
                                                key={type.value}
                                                onClick={() => handleSelect('id_type', type.value)}
                                            >
                                                {type.label}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </Form.Group>
                        </motion.div>

                        <motion.div variants={itemVariants}>
                            <FloatingLabel controlId="id_number" label="ID Number" className="mb-3">
                                <Form.Control
                                    type="text"
                                    name="id_number"
                                    value={formData.id_number}
                                    onChange={handleChange}
                                    placeholder="S1234567A"
                                />
                            </FloatingLabel>
                        </motion.div>

                        {formData.id_type === "PASSPORT" && (
                            <motion.div variants={itemVariants}>
                                <FloatingLabel controlId="passport_number" label="Passport Number" className="mb-4">
                                    <Form.Control
                                        type="text"
                                        name="passport_number"
                                        value={formData.passport_number}
                                        onChange={handleChange}
                                        placeholder="E12345678"
                                    />
                                </FloatingLabel>
                            </motion.div>
                        )}
                    </motion.div>
                );
            case 4:
                return (
                    <motion.div variants={containerVariants} initial="hidden" animate="visible">
                        <motion.div variants={itemVariants}>
                            <h5 className="mb-4">Product Information</h5>
                        </motion.div>

                        <motion.div variants={itemVariants} className="row">
                            <div className="col-md-6">
                                <FloatingLabel controlId="bill_number" label="Bill Number" className="mb-3">
                                    <Form.Control
                                        type="text"
                                        name="bill_number"
                                        value={formData.bill_number}
                                        onChange={handleChange}
                                        placeholder="Auto-generated"
                                        readOnly
                                        style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                                    />
                                </FloatingLabel>
                            </div>

                            <div className="col-md-6">
                                <FloatingLabel controlId="bill_amount" label="Bill Amount" className="mb-3">
                                    <Form.Control
                                        type="number"
                                        name="bill_amount"
                                        value={formData.bill_amount || ""}
                                        onChange={handleChange}
                                        placeholder="Enter Bill amount"
                                    />
                                </FloatingLabel>
                            </div>
                        </motion.div>

                        <motion.div variants={itemVariants} className="row">
                            <div className="col-md-6">
                                <Form.Group className="mb-4">
                                    <Form.Label>EMI Plan</Form.Label>
                                    <Dropdown>
                                        <Dropdown.Toggle
                                            variant="outline-secondary"
                                            id="emi-plan-dropdown"
                                            className="w-100 text-start"
                                        >
                                            {formData.emi_plan_duration
                                                ? (() => {
                                                    const plan = emiPlans.find(p => p.value === formData.emi_plan_duration);
                                                    const monthly =
                                                        formData.bill_amount && !isNaN(formData.bill_amount)
                                                            ? (Number(formData.bill_amount) / Number(formData.emi_plan_duration)).toFixed(2)
                                                            : null;

                                                    return monthly
                                                        ? `${plan.label} (${monthly}/m)`
                                                        : plan.label;
                                                })()
                                                : "Select EMI plan"}
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu className="w-100 p-2 dropdown-scroll">
                                            {emiPlans.map(plan => {
                                                const monthly =
                                                    formData.bill_amount && !isNaN(formData.bill_amount)
                                                        ? (Number(formData.bill_amount) / Number(plan.value)).toFixed(2)
                                                        : null;

                                                return (
                                                    <Dropdown.Item
                                                        key={plan.value}
                                                        onClick={() => handleSelect("emi_plan_duration", plan.value)}
                                                    >
                                                        {monthly ? `${plan.label} (${monthly}/m)` : plan.label}
                                                    </Dropdown.Item>
                                                );
                                            })}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </Form.Group>
                            </div>

                            <div className="col-md-6">
                                <Form.Group className="mb-4">
                                    <Form.Label>Due Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="payment_date"
                                        value={formData.payment_date || ""}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>

                        </motion.div>

                        <motion.div variants={itemVariants}>
                            <Form.Group className="mb-4">
                                <Form.Label>Select Product</Form.Label>
                                <Dropdown>
                                    <Dropdown.Toggle
                                        variant="outline-secondary"
                                        id="product-dropdown"
                                        className="w-100 text-start"
                                    >
                                        {formData.product_id
                                            ? products.find(p => p.product_id === formData.product_id)?.product_name
                                            : "Choose a product"}
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu className="w-100 p-2 dropdown-scroll">
                                        <Form.Control
                                            type="text"
                                            placeholder="Search product..."
                                            className="mb-2"
                                            onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
                                            autoFocus
                                        />

                                        {products
                                            .filter((p) =>
                                                p.product_name.toLowerCase().includes(searchTerm)
                                            )
                                            .map((product) => (
                                                <Dropdown.Item
                                                    key={product.product_id}
                                                    onClick={() => handleSelect("product_id", product.product_id)}
                                                    active={formData.product_id === product.product_id}
                                                >
                                                    {product.product_name}
                                                </Dropdown.Item>
                                            ))}

                                        {products.filter((p) =>
                                            p.product_name.toLowerCase().includes(searchTerm)
                                        ).length === 0 && (
                                                <div className="text-muted px-2 py-1">No products found</div>
                                            )}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </Form.Group>
                        </motion.div>

                    </motion.div>
                );
            case 5:
                return (
                    <motion.div variants={containerVariants} initial="hidden" animate="visible">
                        <motion.div variants={itemVariants}>
                            <h5 className="mb-4">Staff Authentication</h5>
                        </motion.div>

                        <motion.div variants={itemVariants}>
                            <Form.Group className="mb-4">
                                <Form.Label>Select Staff</Form.Label>
                                <Dropdown>
                                    <Dropdown.Toggle
                                        variant="outline-secondary"
                                        id="staff-dropdown"
                                        className="w-100 text-start"
                                    >
                                        {formData.staff_name || "Choose a staff"}
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu className="w-100 p-2 dropdown-scroll">
                                        <Form.Control
                                            type="text"
                                            placeholder="Search staff"
                                            className="mb-2"
                                            onChange={(e) => setStaffSearch(e.target.value.toLowerCase())}
                                            autoFocus
                                        />

                                        {staff
                                            .filter(s => s.user_name.toLowerCase().includes(staffSearch))
                                            .map((s, idx) => (
                                                <Dropdown.Item
                                                    key={idx}
                                                    onClick={() => handleSelect("staff_name", s.user_name)}
                                                    active={formData.staff_name === s.user_name}
                                                >
                                                    {s.user_name}
                                                </Dropdown.Item>
                                            ))}

                                        {staff.filter(s =>
                                            s.user_name.toLowerCase().includes(staffSearch)
                                        ).length === 0 && (
                                                <div className="text-muted px-2 py-1">No staff found</div>
                                            )}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </Form.Group>
                        </motion.div>

                        <FloatingLabel controlId="staff_password" label="Staff Password" className="mb-4 position-relative">
                            <Form.Control
                                type="password"
                                name="staff_password"
                                value={formData.staff_password}
                                onChange={handleChangeStaff}
                                placeholder="Enter staff password"
                                isInvalid={staffAuthStatus === false}
                                isValid={staffAuthStatus === true}
                                style={{
                                    boxShadow:
                                        staffAuthStatus === true
                                            ? "0 0 8px rgba(0, 200, 0, 0.6)"
                                            : staffAuthStatus === false
                                                ? "0 0 8px rgba(255, 0, 0, 0.6)"
                                                : "none",
                                    borderColor:
                                        staffAuthStatus === true
                                            ? "limegreen"
                                            : staffAuthStatus === false
                                                ? "red"
                                                : undefined,
                                }}
                            />
                        </FloatingLabel>
                    </motion.div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="d-flex min-vh-100">
            <Helmet>
                <title>Sign Up | Dashboard</title>
                <style>{`
                    .loading-overlay {
                        position: fixed;
                        top: 0;
                        left: 0;
                        z-index: 1050;
                        width: 100vw;
                        height: 100vh;
                        background-color: rgba(255, 255, 255, 0.7);
                        display: flex;
                        justify-content: center;
                        align-items: center;
                    }
                `}</style>
            </Helmet>
            <ToastContainer />

            {isSubmitting && (
                <div className="loading-overlay">
                    <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            )}

            <Col md={6} className="d-none d-md-flex flex-column justify-content-center align-items-center p-5"
                style={{
                    background: 'linear-gradient(135deg, #221c8bff 0%,rgba(255, 89, 172, 1) 100%)',
                    position: 'relative',
                    overflow: 'hidden'
                }}>

                <div style={{
                    position: 'absolute',
                    top: '-50px',
                    right: '-50px',
                    width: '200px',
                    height: '200px',
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.1)'
                }}></div>

                <div style={{
                    position: 'absolute',
                    bottom: '-80px',
                    left: '-80px',
                    width: '300px',
                    height: '300px',
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.1)'
                }}></div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-center"
                    style={{ zIndex: 1, maxWidth: "500px" }}
                >
                    <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 6, repeat: Infinity }}>
                        <img src={logo} alt="Dashboard UI" className="img-fluid mb-4" style={{ maxWidth: "80px" }} />
                    </motion.div>

                    <h2 className="text-white mb-3 fw-bold">
                        Welcome, {formData.full_name || "User"}
                    </h2>

                    <p className="text-white-50 mb-4">
                        {formData.email_address
                            ? <>We'll contact you at <b>{formData.email_address}</b></>
                            : "Enter your details to get started"}
                    </p>

                    <div className="live-preview text-start">
                        <h5 className="text-white mb-3">Registration Preview</h5>

                        <p><b>Full Name:</b> {formData.full_name || "—"}</p>
                        <p><b>Email:</b> {formData.email_address || "—"}</p>
                        <p><b>Phone:</b> {formData.phone_number || "—"}</p>
                        <p><b>DOB:</b> {formData.date_of_birth || "—"}</p>
                        {/* <p>
                            <b>Password:</b>{" "}
                            {formData.password
                                ? (formData.password.length > 4
                                    ? formData.password.slice(0, 2) +
                                    "*".repeat(formData.password.length - 4) +
                                    formData.password.slice(-2)
                                    : "*".repeat(formData.password.length))
                                : "—"}
                        </p> */}
                        <p><b>ID Type:</b> {formData.id_type || "—"}</p>
                        <p><b>ID Number:</b> {formData.id_number || "—"}</p>

                        <p><b>Bill No:</b> {formData.bill_number || "—"}</p>
                        <p><b>Bill Amount:</b> {formData.bill_amount || "—"}</p>

                        <p>
                            <b>Product:</b>{" "}
                            {products.find(p => p.product_id === formData.product_id)?.product_name || "—"}
                        </p>
                        <p>
                            <b>EMI Plan:</b>{" "}
                            {formData.emi_plan_duration
                                ? `${formData.emi_plan_duration} months`
                                : "—"}
                        </p>
                        <p><b>Due date:</b> {formData.payment_date || "—"}</p>

                        <p><b>Staff:</b> {formData.staff_name || "—"}</p>
                        <p>
                            <b>Staff Auth:</b>{" "}
                            {staffAuthStatus === true
                                ? "Verified"
                                : staffAuthStatus === false
                                    ? "Invalid"
                                    : "Pending"}
                        </p>
                    </div>
                </motion.div>

            </Col>

            <Col md={6} className="d-flex justify-content-center align-items-center p-4">
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="w-100 bg-white rounded-4 shadow-sm p-4 p-md-5"
                    style={{
                        maxWidth: "500px",
                        border: '1px solid rgba(0, 0, 0, 0.05)'
                    }}
                >
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        className="text-center mb-4"
                    >
                        <h3 className="fw-bold mb-3" style={{ color: '#1e293b', fontSize: '1.75rem' }}>Create Your Account</h3>
                        <p className="text-muted" style={{ fontSize: '1rem' }}>Complete all steps to register</p>
                    </motion.div>

                    <div className="mb-4">
                        <div className="d-flex justify-content-between mb-2">
                            <small>Step {currentSection} of 5</small>
                            <small>{Math.round(completionPercentage())}% Complete</small>
                        </div>
                        <ProgressBar
                            now={completionPercentage()}
                            variant="primary"
                            className="rounded-pill"
                            style={{ height: '6px' }}
                        />
                    </div>

                    <Form onSubmit={currentSection === 5 ? handleSubmit : handleNext}>
                        {renderSection()}

                        <div className="d-flex justify-content-between mt-4">
                            {currentSection > 1 ? (
                                <Button
                                    variant="outline-secondary"
                                    onClick={handlePrev}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        borderRadius: '0.5rem'
                                    }}
                                >
                                    <FaChevronLeft className="me-1" /> Back
                                </Button>
                            ) : (
                                <div></div>
                            )}

                            {currentSection < 5 ? (
                                <motion.div
                                    whileHover={{ y: -2 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        style={{
                                            padding: '0.75rem 1.5rem',
                                            borderRadius: '0.5rem',
                                            background: 'linear-gradient(135deg, #4f46e5 0%,rgb(58, 61, 237) 100%)',
                                            border: 'none'
                                        }}
                                    >
                                        Next <FaChevronRight className="ms-1" />
                                    </Button>
                                </motion.div>
                            ) : (
                                <motion.div
                                    whileHover={{ y: -2 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        style={{
                                            padding: '0.75rem 1.5rem',
                                            borderRadius: '0.5rem',
                                            background: 'linear-gradient(135deg, #4f46e5 0%,rgb(58, 61, 237) 100%)',
                                            border: 'none'
                                        }}
                                        disabled={isSubmitting || staffAuthStatus !== true}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                Creating Account...
                                            </>
                                        ) : (
                                            <>
                                                <FaCheck className="me-2" /> Register
                                            </>
                                        )}
                                    </Button>

                                </motion.div>
                            )}
                        </div>
                    </Form>

                    <motion.div
                        variants={itemVariants}
                        className="text-center mt-4"
                    >
                        <p className="text-muted">
                            Already have an account? <Link to="/sign-in" className="text-primary fw-bold text-decoration-none">Sign In</Link>
                        </p>
                    </motion.div>
                </motion.div>
            </Col>
        </div>
    );
};

export default SignUp;